import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaAward,
  FaChartBar,
  FaLightbulb,
  FaDownload,
  FaRedo,
  FaHome,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Loader/Loader';
import './ResultPage.css';

const ResultPage = () => {
  const navigate = useNavigate();
  const { examId } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showRubric, setShowRubric] = useState(false);

  useEffect(() => {
    const fetchExam = async () => {
      try {
        const res = await axios.get(api.getExamById(examId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          setExam(res.data.data);
        }
      } catch (error) {
        console.error('Error fetching exam result:', error);
        toast.error('Failed to load exam result');
        navigate('/university/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchExam();
  }, [examId, token, navigate]);

  const handleGenerateCertificate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${api.baseURL}/api/exams/certificates/generate`,
        { examId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success('Certificate generated!');
        navigate('/university/certificates');
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error(error.response?.data?.message || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  const isPassed = (exam?.percentage || 0) >= 45;
  const grade = getGrade(exam?.percentage || 0);

  const typeBreakdown = useMemo(() => {
    if (!exam?.questions?.length) return [];

    const grouped = exam.questions.reduce((acc, q, i) => {
      const key = q.type || 'other';
      if (!acc[key]) {
        acc[key] = {
          type: key,
          totalQuestions: 0,
          totalMarks: 0,
          obtainedMarks: 0,
        };
      }
      acc[key].totalQuestions += 1;
      acc[key].totalMarks += q.marks || 0;
      acc[key].obtainedMarks += exam.answers?.[i]?.marksAwarded || 0;
      return acc;
    }, {});

    return Object.values(grouped);
  }, [exam]);

  const skillGapPlan = useMemo(() => {
    const weakAreas = exam?.evaluation?.weakAreas || [];
    if (!weakAreas.length) {
      return ['Maintain consistency with timed mock tests', 'Focus on improving answer clarity and structure'];
    }
    return weakAreas.slice(0, 5).map((area) => `Revise ${area} with 2 focused practice sets this week`);
  }, [exam]);

  const recommendation = useMemo(() => {
    const percentage = exam?.percentage || 0;
    if (percentage >= 80) return 'Strong score. Try Post Graduation level for a harder challenge.';
    if (percentage >= 60) return 'Good performance. Retake this level once after revising weak areas for a better score.';
    if (percentage >= 45) return 'You passed, but a retake at the same level is recommended to strengthen fundamentals.';
    return 'Retake at the same level after revising weak areas, then move forward once you consistently cross 60%.';
  }, [exam]);

  const typeLabel = (type) => {
    if (type === 'mcq') return 'MCQ';
    if (type === 'one-word') return 'One Word';
    if (type === 'short-answer') return 'Short Answer';
    if (type === 'descriptive') return 'Scenario';
    if (type === 'long-answer') return 'Long Answer';
    return type;
  };

  const getExpectedAnswer = (question, answer) => {
    const expectedAnswer = [
      question?.correctAnswer,
      question?.expectedAnswer,
      question?.modelAnswer,
      answer?.expectedAnswer,
      answer?.correctAnswer,
      answer?.modelAnswer,
    ].find((value) => typeof value === 'string' && value.trim());

    return expectedAnswer || 'Expected answer is not available for this question.';
  };

  const isWrittenQuestion = (type) =>
    ['one-word', 'short-answer', 'descriptive', 'long-answer'].includes(type);

  if (loading) return <Loader message="Loading result..." />;
  if (!exam) return <div>Exam not found</div>;

  return (
    <div className="result-page">
      <header className="result-header">
        <button className="result-back-btn" onClick={() => navigate('/university/dashboard')}>
          <FaArrowLeft /> Dashboard
        </button>
        <h1>Exam Result</h1>
        <div></div>
      </header>

      {/* Score Card */}
      <div className="result-score-section">
        <div className={`result-score-card ${isPassed ? 'passed' : 'failed'}`}>
          <div className="result-score-circle">
            <svg viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="#e2e8f0" strokeWidth="8" />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke={isPassed ? '#16a34a' : '#dc2626'}
                strokeWidth="8"
                strokeDasharray={`${exam.percentage * 2.83} 283`}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
              />
            </svg>
            <div className="result-score-text">
              <span className="result-percentage">{exam.percentage?.toFixed(0)}%</span>
              <span className="result-grade">{grade}</span>
            </div>
          </div>
          <div className="result-score-details">
            <h2>{exam.subject}</h2>
            <p className="result-category">{exam.category} → {exam.subcategory}</p>
            <div className="result-meta">
              <span className={`result-level result-diff-${exam.level}`}>{exam.level}</span>
              <span className={`result-status ${isPassed ? 'result-passed' : 'result-failed'}`}>
                {isPassed ? '✓ PASSED' : '✗ FAILED'}
              </span>
            </div>
            <div className="result-marks">
              <span>{exam.obtainedMarks} / {exam.totalMarks} marks</span>
            </div>
          </div>
        </div>
      </div>

      {/* Evaluation Feedback */}
      {exam.evaluation && (
        <div className="result-feedback-section">
          {/* Overall Feedback */}
          <div className="result-feedback-card">
            <h3><FaLightbulb /> AI Feedback</h3>
            <p>{exam.evaluation.overallFeedback}</p>
          </div>

          {/* Strong & Weak Areas */}
          <div className="result-areas-grid">
            {exam.evaluation.strongAreas?.length > 0 && (
              <div className="result-area-card result-strong">
                <h4><FaCheck /> Strong Areas</h4>
                <ul>
                  {exam.evaluation.strongAreas.map((area, i) => (
                    <li key={i}>{area}</li>
                  ))}
                </ul>
              </div>
            )}
            {exam.evaluation.weakAreas?.length > 0 && (
              <div className="result-area-card result-weak">
                <h4><FaTimes /> Areas to Improve</h4>
                <ul>
                  {exam.evaluation.weakAreas.map((area, i) => (
                    <li key={i}>{area}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {exam.evaluation.suggestions?.length > 0 && (
            <div className="result-suggestions-card">
              <h4><FaLightbulb /> Suggestions</h4>
              <ul>
                {exam.evaluation.suggestions.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="result-breakdown-card">
            <h4><FaChartBar /> Detailed Breakdown by Question Type</h4>
            <div className="result-breakdown-grid">
              {typeBreakdown.map((item) => {
                const score = item.totalMarks ? Math.round((item.obtainedMarks / item.totalMarks) * 100) : 0;
                return (
                  <div key={item.type} className="result-breakdown-item">
                    <strong>{typeLabel(item.type)}</strong>
                    <span>{item.totalQuestions} questions</span>
                    <span>{item.obtainedMarks}/{item.totalMarks} marks</span>
                    <span>{score}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="result-suggestions-card">
            <h4><FaRedo /> Retake Recommendation</h4>
            <p>{recommendation}</p>
          </div>

          <div className="result-suggestions-card result-skill-gap-card">
            <h4><FaLightbulb /> Skill Gap Insights</h4>
            <ul>
              {skillGapPlan.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
          </div>

          <div className="result-rubric-card">
            <button type="button" onClick={() => setShowRubric((prev) => !prev)}>
              {showRubric ? 'Hide' : 'Show'} Evaluation Rubric & Model Answer Guidance
            </button>
            {showRubric && (
              <div className="result-rubric-content">
                <p>Rubric used by evaluator:</p>
                <ul>
                  <li>Accuracy of concept and facts</li>
                  <li>Logical reasoning and approach</li>
                  <li>Clarity and structured explanation</li>
                  <li>Completeness and relevance to question</li>
                </ul>
                <p>
                  Model answer guidance is shown per question as Expected Answer / Correct Option. Equivalent logically correct phrasing can still earn marks.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Question Review */}
      <div className="result-questions-section">
        <h3><FaChartBar /> Question Review</h3>
        {exam.questions.map((q, i) => {
          const answer = exam.answers?.[i];
          return (
            <div key={i} className={`result-question-card ${answer?.isCorrect ? 'correct' : 'incorrect'}`}>
              <div className="result-q-header">
                <span className="result-q-num">Q{i + 1}</span>
                <span className={`result-q-status ${answer?.isCorrect ? 'correct' : 'incorrect'}`}>
                  {answer?.isCorrect ? <><FaCheck /> Correct</> : <><FaTimes /> Incorrect</>}
                </span>
                <span className="result-q-marks">{answer?.marksAwarded || 0}/{q.marks}</span>
              </div>
              <p className="result-q-text">{q.questionText}</p>

              {q.type === 'mcq' && (
                <div className="result-q-options">
                  {q.options?.map((opt, oi) => (
                    <div
                      key={oi}
                      className={`result-q-option 
                        ${opt === q.correctAnswer ? 'correct-answer' : ''} 
                        ${opt === answer?.userAnswer && opt !== q.correctAnswer ? 'wrong-answer' : ''}
                      `}
                    >
                      {opt.replace(/^[A-D]\)\s*/, '')}
                      {opt === q.correctAnswer && <FaCheck className="result-correct-icon" />}
                      {opt === answer?.userAnswer && opt !== q.correctAnswer && <FaTimes className="result-wrong-icon" />}
                    </div>
                  ))}
                </div>
              )}

              {isWrittenQuestion(q.type) && (
                <div className="result-q-answer-text">
                  <div className="result-user-answer">
                    <strong>Your Answer:</strong>
                    <p>{answer?.userAnswer || '(Question not answered)'}</p>
                  </div>
                  <div className="result-correct-answer">
                    <strong>Expected Answer:</strong>
                    <p>{getExpectedAnswer(q, answer)}</p>
                  </div>
                </div>
              )}

              {answer?.feedback && (
                <div className="result-q-feedback">
                  <FaLightbulb /> {answer.feedback}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="result-actions">
        {isPassed && (
          <button className="result-action-btn result-cert-btn" onClick={handleGenerateCertificate} disabled={generating}>
            <FaAward /> {generating ? 'Generating...' : 'Get Certificate'}
          </button>
        )}
        <button className="result-action-btn result-retry-btn" onClick={() => navigate('/university/exam')}>
          <FaRedo /> Take Another Exam
        </button>
        <button className="result-action-btn result-home-btn" onClick={() => navigate('/university/dashboard')}>
          <FaHome /> Dashboard
        </button>
      </div>
    </div>
  );
};

function getGrade(percentage) {
  if (percentage >= 95) return 'A+';
  if (percentage >= 85) return 'A';
  if (percentage >= 75) return 'B+';
  if (percentage >= 65) return 'B';
  if (percentage >= 55) return 'C+';
  if (percentage >= 45) return 'C';
  if (percentage >= 35) return 'D';
  return 'F';
}

export default ResultPage;
